import {
  computed,
  reactive,
  Ref,
  ref,
  UnwrapRef,
  watch,
  ComponentInternalInstance,
  getCurrentInstance,
  ComputedRef,
  unref,
} from '@vue/composition-api';

import extraFileInfoHandler from '@/renderer/components/SpacePage/compositions/extraFileInfoHandler';

import i18n, { i18n_t } from '@/locales/i18n';
import useImgPre from '@/renderer/components/SpacePage/compositions/useImagePreview';
import tips from '@/renderer/scripts/utils/tips';
import smh, { SMH } from '@/renderer/scripts/smh';
import ipc from '@/renderer/scripts/utils/ipc';
import useSort, { Sort } from '@/renderer/components/SpacePage/compositions/useSort';

type TableData = {
  list: SMH.FavoriteFileItemData[];
  selectedRowKeys: Array<Number | String>;
  selectedList: SMH.FavoriteFileItemData[];
};
type Item = {
  id: number;
  spaceId?: string;
  name?: string;
  path?: Array<string>;
  type?: string;
  creationTime?: string;
  userId?: number;
  userName?: string;
  favoriteId: number;
};
type PageData = {
  currPage: number;
  pageSize: number;
  total: number;
  pageSizeOptions: Array<any>;
};

type Handler = {
  tableData: UnwrapRef<TableData>;
  pageData: UnwrapRef<PageData>;
  loading: Ref<boolean>;
  loadingOnHeader: Ref<boolean>; // 在顶部loading，loading期间list内容不变
  sort: UnwrapRef<Sort>;
  columns: ComputedRef<Array<any>>;
  methods: {
    pullList: () => Promise<void>;
    refresh: () => void;
    silentRefresh: () => void;
    clearSelectedAndPullList: () => void;
    onCurrentChange: (current: number) => void;
    sortChange: (newSort: Sort) => void;
    switchSort: (columnName: string) => void;
  };
};
export default function sendShareListHandle(): Handler {
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const store = vm.$store;
  const isMobileStyle = computed(() => store.getters.isMobileStyle);
  const { methods: extraFileInfoMethods } = extraFileInfoHandler();
  const generalColumns = [
    {
      colKey: 'name',
      title: i18n_t('项目名称'),
      width: 200,
      ellipsis: {
        disabled: true,
      },
    },
    {
      colKey: 'path',
      title: i18n_t('位置'),
      width: 130,
    },
    {
      colKey: 'favoriteTime',
      title: 'title-favoriteTime',
      titleDesc: i18n_t('收藏时间'),
      width: 150,
      sortType: 'all',
      sorter: true,
      ellipsis: true,
    },
    {
      colKey: 'size',
      title: i18n_t('大小'),
      width: 150,
      // 由于此栏承载了操作按钮组，因此宽度必须 > 150px
      fixed: 'right',
    },
  ];
  const mobileColumns = [
    {
      colKey: 'mobile-name',
      title: i18n_t('全选'),
      ellipsis: true,
      width: 180,
    },
    {
      colKey: 'mobile-operate',
      title: '',
      width: 70,
      align: 'right',
    },
  ];
  // 初始化columns
  const columns = computed(() => (isMobileStyle.value ? mobileColumns : generalColumns));
  const tableData = reactive<TableData>({
    list: [],
    selectedRowKeys: [],
    selectedList: [],
  });
  const pageData = reactive<PageData>({
    currPage: 1,
    pageSize: 10,
    total: 0,
    pageSizeOptions: [], // 值为[]即不显示
  });

  const loading = ref(false);
  const loadingOnHeader = ref(false);

  const {
    innerSort,
    tableSort,
    methods: sortMethods,
  } = useSort('workbench', {
    sortBy: undefined,
    descending: true,
  });
  const methods = {
    async pullList(silentLoading = false) {
      const startTime = new Date().getTime();
      const opt = {
        page: pageData.currPage,
        pageSize: pageData.pageSize,
        orderBy: innerSort.value.orderBy,
        orderByType: innerSort.value.orderByType,
      };
      try {
        loading.value = !silentLoading;
        loadingOnHeader.value = silentLoading;
        const { data: listRes } = await smh.userAPI.getFavoriteList(opt);
        pageData.total = listRes.totalNum;
        ipc.emit(ipc.RENDERER_EVENTS_MAP.ON_DATA_LOADED, pageData.total);
        tableData.list = listRes.contents;
        extraFileInfoMethods.getFileExtraRefresh(tableData, { multiSpaceId: true });
        const { getImagePre } = useImgPre();
        getImagePre(tableData, tableData.list, 0, 20, 20);
        if (loadingOnHeader.value) {
          tips.success(i18n_t('刷新成功'));
        }
        loading.value = false;
        loadingOnHeader.value = false;
        reporter.report('list_cost_time', {
          page: 'MyFavorite',
          cost: new Date().getTime() - startTime,
          count: pageData.pageSize,
        });
      } catch (err) {
        tips.error(err as MsgError);
      }
    },
    refresh() {
      tableData.list = [];
      tableData.selectedRowKeys = [];
      tableData.selectedList = [];
      methods.pullList();
    },
    silentRefresh() {
      if (!loading.value && !loadingOnHeader.value) {
        tableData.selectedRowKeys = [];
        tableData.selectedList = [];
        methods.pullList(true);
      }
    },
    clearSelectedAndPullList() {
      tableData.selectedRowKeys = [];
      tableData.selectedList = [];
      methods.pullList();
    },
    onCurrentChange(current: number) {
      pageData.currPage = current;
      methods.pullList();
    },
    switchSort(columnName: string) {
      // 升序→降序→无序→升序......
      methods.sortChange(sortMethods.switchSort(columnName));
    },
    sortChange(newSort: Sort | undefined) {
      sortMethods.sortChange(newSort);
      methods.pullList();
    },
  };
  methods.pullList();
  watch(
    () => pageData.total,
    val => {
      if (!val) return;
      const newPageNum = Math.ceil(val / pageData.pageSize);
      if (pageData.currPage > newPageNum) {
        pageData.currPage = newPageNum;
        methods.refresh();
      }
    },
  );
  watch(
    () => tableData.selectedList,
    newList => {
      ipc.emit(ipc.RENDERER_EVENTS_MAP.ON_SELECTED_CHANGE, unref(newList));
    },
  );
  return {
    columns,
    tableData,
    pageData,
    loading,
    loadingOnHeader,
    sort: tableSort,
    methods,
  };
}
